import { NavLink, Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { can as hasPermission, useSession } from '@/shared/auth/session'

/**
 * Dossier & Intelligence — the module shell.
 *
 * ## The tab order is the working order
 *
 * **The Assay** is the landing board and the module's index route: what the record rests on,
 * before which rows are in it. **Subjects** is the register — who this platform holds a file
 * on. **New target** opens one. Everything after is the machinery, grouped the way an analyst's day splits: what is
 * being collected, what is being entered by hand, what needs triaging, and the
 * administration that governs all of it.
 *
 * ## A tab the caller cannot use is not shown
 *
 * Following the rest of the app: a `program:read` user was once shown eleven links that all
 * rendered a Forbidden page. What is NOT filtered is the run monitor — seeing that
 * collection is failing is part of `intel:read`, because a pipeline whose health is only
 * visible to whoever can change it is a pipeline nobody questions.
 */

interface Tab {
  to: string
  label: string
  hint: string
  permission?: string
  end?: boolean
}

interface Group {
  label: string
  tabs: Tab[]
}

export const DOSSIER_GROUPS: Group[] = [
  {
    label: 'Standing',
    tabs: [
      { to: '/dossier', label: 'The Assay', end: true,
        hint: 'What the record rests on — Admiralty standing, custody delay and the gaps' },
    ],
  },
  {
    label: 'Files',
    tabs: [
      { to: '/dossier/analytics', label: 'Analytics',
        hint: 'What the record rests on, in plain language — and the council' },
      { to: '/dossier/subjects', label: 'Subjects', hint: 'Every file this platform holds' },
      { to: '/dossier/new', label: 'New target', hint: 'Open a file — on anybody or anything',
        permission: 'intel:create' },
      { to: '/dossier/gaps', label: 'Gaps', hint: 'What is missing, and what to go and find' },
    ],
  },
  {
    label: 'Collection',
    tabs: [
      { to: '/dossier/plans', label: 'Collection plans', hint: 'What is collected, and why',
        permission: 'intel:collect' },
      { to: '/dossier/runs', label: 'Run monitor', hint: 'Every run and why it ended as it did' },
      { to: '/dossier/sources', label: 'Sources & grading',
        hint: 'Publications, their drivers and their Admiralty reliability' },
    ],
  },
  {
    label: 'Entry & triage',
    tabs: [
      { to: '/dossier/entry', label: 'Manual entry', hint: 'Enter an observation, attested',
        permission: 'intel:ingest' },
      { to: '/dossier/import', label: 'Bulk import', hint: 'CSV or JSON, dry run then apply',
        permission: 'intel:ingest' },
      { to: '/dossier/triage', label: 'Triage queue', hint: 'Unverified items, oldest first',
        permission: 'intel:triage' },
      { to: '/dossier/watches', label: 'Watches', hint: 'Standing questions',
        permission: 'intel:watch' },
      { to: '/dossier/alerts', label: 'Alerts', hint: 'What the watches found' },
    ],
  },
]

export function DossierLayout() {
  const authorities = useSession((s) => s.authorities)

  return (
    <div className="space-y-3">
      <nav className="axis-table-scroll flex gap-4 border-b border-border" aria-label="Dossier">
        {DOSSIER_GROUPS.map((group) => {
          const visible = group.tabs.filter(
            (tab) => hasPermission(authorities, tab.permission))
          if (visible.length === 0) return null
          return (
            <div key={group.label} className="flex items-center gap-0.5">
              <span className="pr-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                {group.label}
              </span>
              {visible.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  title={tab.hint}
                  className={({ isActive }) => cn(
                    'shrink-0 whitespace-nowrap border-b-2 px-2 py-1.5 text-[12px]',
                    isActive
                      ? 'border-primary font-medium text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground')}
                >
                  {tab.label}
                </NavLink>
              ))}
            </div>
          )
        })}
      </nav>
      <Outlet />
    </div>
  )
}
